import axios from 'axios';
import config from '../config';

export type InsightTone = 'positive' | 'warning' | 'info';

export interface InsightItem {
  id: string;
  title: string;
  description: string;
  action: string;
  tone: InsightTone;
}

export interface SalesMomentum {
  changePercent: number;
  trend: 'up' | 'down' | 'flat';
  lastAverage: number;
  prevAverage: number;
  window: number;
}

export interface ChannelSplit {
  label: string;
  value: number;
  percent: number;
  color: string;
}

export interface WeekdayPerformanceSummary {
  entries: { weekday: string; value: number; percent: number }[];
  best: { weekday: string; value: number };
  worst: { weekday: string; value: number };
}

export interface SalesSummaryPayload {
  startDate: string;
  endDate: string;
  totalSales: number;
  totalOrders: number;
  averageOrderValue: number;
  onlineSales: number;
  retailSales: number;
  returningCustomerRate: number;
  grossMarginRate: number;
}

export interface SalesInsightsResponse {
  summary: SalesSummaryPayload | null;
  momentum: SalesMomentum | null;
  weekdayPerformance: WeekdayPerformanceSummary | null;
  insights: InsightItem[];
  channelMix: ChannelSplit[];
}

export interface PeriodComparisonRecord {
  startDate: string;
  endDate: string;
  totalSales: number;
  totalOrders: number;
  averageOrderValue: number;
  onlineSales: number;
  retailSales: number;
}

export interface PeriodComparisonResponse {
  current: PeriodComparisonRecord | null;
  previous: PeriodComparisonRecord | null;
  yearOverYear: PeriodComparisonRecord | null;
  duration: number;
}

export const fetchSalesInsights = async (
  startDate: string,
  endDate: string,
): Promise<SalesInsightsResponse> => {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${config.API_URL}/sales/insights`, {
    params: {
      startDate,
      endDate,
    },
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : undefined,
  });

  return response.data;
};

export const fetchPeriodComparison = async (
  startDate: string,
  endDate: string,
): Promise<PeriodComparisonResponse> => {
  const token = localStorage.getItem('token');
  const response = await axios.get(
    `${config.API_URL}/sales/period-comparison`,
    {
      params: {
        startDate,
        endDate,
      },
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined,
    },
  );

  return response.data;
};
